import React, { useState, useContext, useCallback, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import * as S from "./styles"

import { mapRuleDecisionName, mapRuleNameByStep, mapRuleDecisionByStep, mapRuleTypeByStep, DEFAULT_RISK_CLASSIFICATION_LEVEL } from './rules_utils'
import AuthContext from "../../context/auth-context";

import { LoadingPage } from "../AuxiliarPages/LoadingPage/LoadingPage";
import { NoAccessPage } from "../AuxiliarPages/NoAccessPage/NoAccessPage";
import { ErrorPage } from "../AuxiliarPages/ErrorPage/ErrorPage";

import TextButton from "./components/TextButton"
import BaseButton from '../../components/BaseButton'
import IconButton from "./components/IconButton";
import RuleCreationDialog from './RuleCreationDialog/RuleCreationDialog'
import BooleanRuleCard from './components/BooleanRuleCard'
import NumericRuleCard from "./components/NumericRuleCard";
import ListPresenceRuleCard from "./components/ListPresenceRuleCard"
import RiskLevelRuleCard from "./components/RiskLevelRuleCard"
import CompositeRuleCard from "./components/CompositeRuleCard"

export const RulesPage = () => {

  const navigate = useNavigate();
  const userData = useContext(AuthContext).user_data
  const userRoles = userData.roles;
  const company_key = userData.company_key;

  const analyst = userData.email || userData.username

  if (!userRoles.includes("manage_rules")) {
    navigate("/envelope-inbox");
  }

  const [companyData, setCompanyData] = useState(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState({
    hasError: false,
    statusCode: 200,
  });

  const [rules, setRules] = useState([])
  const [internalRules, setInternalRules] = useState([])

  const [lastEditingRules, setLastEditingRules] = useState([])
  const [ruleEditingMode, setRuleEditingMode] = useState(false)
  const [ruleBeingEdited, setRuleBeingEdited] = useState()

  const [openRuleCreationDialog, setOpenRuleCreationDialog] = useState(false)

  const [submitType, setSubmitType] = useState('')

  const activeEditingMode = () => {
    setRuleEditingMode(true)
    setLastEditingRules(rules)
  }

  const cancelEditingMode = () => {
    setRules(lastEditingRules)
    setSubmitType('')
    setRuleEditingMode(false)
  }

  const openRuleCreation = () => {
    setRuleBeingEdited(undefined)
    setSubmitType('add')
    setOpenRuleCreationDialog(true)
  }

  const editRule = (rule) => {
    setRuleBeingEdited(rule)
    setSubmitType('edit')
    setOpenRuleCreationDialog(true)
  }

  const deleteRule = (index) => {
    if (index >= 0 && index < rules.length) {
      const rulesModified = [...rules];
      rulesModified.splice(index, 1);
      setRules(rulesModified);
    }
  }

  const saveRules = () => {
    requestCompanyAnalysisRules()
  }

  const validateRules = (newRule, submitType) => {
    let message = 'Erro ao adicionar regra'
    let validated = false
    const sameStepRules = rules.filter(rule => rule.step === newRule.step)

    switch (submitType) {
      case 'add':
        if (newRule.type === 'composite' && sameStepRules.length > 0) {
          validated = false
          message = 'Esta regra permite apenas uma configuração. Edite a regra existente.'
          return {
            message,
            validated
          }
        }
        if (sameStepRules.length > 0) {

          const ruleWithSameDecision = sameStepRules.find(
            rule => rule.parameters[0].decision === newRule.parameters[0].decision
          )
          if (ruleWithSameDecision) {
            validated = false
            message = 'Regra já existe. Não é possível adicionar uma regra duplicada.'
            return {
              message,
              validated
            }
          } else {
            if (newRule.type === 'list_presence') {
              let newRuleFlags = newRule.parameters[0].value
              let ruleWithFlagsRepeated = sameStepRules.filter((rule) => (rule.parameters[0].value.filter((flag) => newRuleFlags.includes(flag)) || []).length > 0)

              if (ruleWithFlagsRepeated.length > 0) {
                validated = false
                message = 'Regras de diferentes decisões, mas mesmo tippos não podem ter indicadores em comum.'
                return {
                  message,
                  validated
                }
              }
            }

            validated = true
            message = 'Regra adicionada'
          }

        } else {
          validated = true
          message = 'Regra adicionada'
        }
        break;
      case 'edit':
        if (newRule.type === 'list_presence') {
          if ((sameStepRules || []).length > 0) {

            const ruleWithDifferentDecisionSameStep = sameStepRules.find(
              rule => rule.parameters[0].decision !== newRule.parameters[0].decision
            )
            if ((ruleWithDifferentDecisionSameStep || []).length > 0) {
              let newRuleFlags = newRule.parameters[0].value
              let ruleWithFlagsRepeated = ruleWithDifferentDecisionSameStep.filter((rule) => (rule.parameters[0].value.filter((flag) => newRuleFlags.includes(flag)) || []).length > 0)

              if ((ruleWithFlagsRepeated || []).length > 0) {
                validated = false
                message = 'Regras de diferentes decisões, mas mesmo tippos não podem ter indicadores em comum.'
                return {
                  message,
                  validated
                }
              }
            }
          }
        }

        validated = true
        message = 'Regra alterada'
        break
      default:
        break
    }

    return {
      message,
      validated
    }
  }

  const submitRule = (rule, submitType) => {
    let submitRuleWithSuccess = false

    const { validated, message } = validateRules(rule, submitType)

    if (!validated)
      return handleToast(message, 'warning')

    switch (submitType) {
      case 'add':
        setRules((prev) => [rule, ...prev])
        resetDialog()
        submitRuleWithSuccess = true
        break;
      case 'edit':
        let index = rules.findIndex((item) => item.step === rule.step && item.parameters[0].decision === rule.parameters[0].decision);
        if (index !== -1) {
          const rulesModified = [...rules];
          rulesModified.splice(index, 1, rule);
          setRules(rulesModified);
          resetDialog()
          submitRuleWithSuccess = true;
        } else {
          let index = rules.findIndex((item) => item.step === rule.step);
          if (index !== -1) {
            const rulesModified = [...rules];
            rulesModified.splice(index, 1, rule);
            setRules(rulesModified);
            resetDialog()
          } else {
            handleToast('Regra não encontrada para edição', 'warning');
          }
        }
        break
      default:
        return false
    }

    return submitRuleWithSuccess
  }

  const handleToast = (message, type) => {
    if (type === "success") {
      toast.success(message, {
        position: "top-center",
        autoClose: 2500,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      })
    } else if (type === "error") {
      toast.error('Ocorreu um erro ao criar o envelope. Tente novamente.', {
        position: "top-center",
        autoClose: 2500,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      })
    } else if (type === 'warning') {
      toast.warning(message, {
        position: "top-right",
        autoClose: 2500,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      })
    }
  }

  const requestCompanyData = useCallback((company_key) => {
    setLoading(true)
    const timer = setTimeout(() => {
      axios
        .get(`/dash/sign/company/${company_key}`)
        .then((response) => {
          if (response.data.analysis_rules?.rules) {
            setRules(response.data.analysis_rules?.rules)
            setInternalRules(response.data.analysis_rules?.internal_rules ?? [])
          }
          setCompanyData(response.data);
        })
        .catch((error) => {
          if ([404].includes((error.response || {}).status)) {
            setError({
              has_error: true,
              status: error.response.status,
            });
          } else {
            setError({
              has_error: true,
              status: 500,
            });
          }
        })
        .finally(() => {
          setLoading(false);
        });
    }, 500);
    return () => {
      clearTimeout(timer);
    };
  }, []);

  const requestCompanyAnalysisRules = useCallback(() => {
    setLoading(true)
    const rulesPayload = {
      "analysis_rules": {
        "rules": rules,
        "analyst": analyst || '',
      }
    }
    const timer = setTimeout(() => {
      axios
        .patch(`/dash/sign/company/${company_key}`, rulesPayload)
        .then((response) => {
          if (response.data.analysis_rules?.rules) {
            setRules(response.data.analysis_rules?.rules)
          }
          setLastEditingRules(rules)
          setSubmitType('')
          setRuleEditingMode(false)
        })
        .catch((error) => {
          if ([404].includes((error.response || {}).status)) {
            setError({
              has_error: true,
              status: error.response.status,
            });
          } else {
            setError({
              has_error: true,
              status: 500,
            });
          }
        })
        .finally(() => {
          setLoading(false);
        });
    }, 500);
    return () => {
      clearTimeout(timer);
    };
  }, [rules, analyst, company_key]);

  useEffect(() => {
    requestCompanyData(company_key);
  }, [requestCompanyData, company_key]);

  const resetDialog = () => {
    setOpenRuleCreationDialog(false)
  }

  if (!userRoles.includes("manage_rules")) {
    return (
      <S.RulePageContainer>
        <NoAccessPage />
      </S.RulePageContainer>
    );
  }
  if (loading) {
    return (
      <S.RulePageContainer>
        <LoadingPage />
      </S.RulePageContainer>
    );
  } else if (error.hasError) {
    return (
      <S.RulePageContainer>
        <ErrorPage />
      </S.RulePageContainer>
    );
  } else if (companyData) {
    return (
      <>
        <ToastContainer
          position="top-center"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
        {
          userRoles.includes("manage_rules") ? (
            <div className="page-container">
              {
                openRuleCreationDialog &&
                <RuleCreationDialog
                  open={openRuleCreationDialog}
                  setOpen={setOpenRuleCreationDialog}
                  handleToast={handleToast}
                  submitRule={submitRule}
                  submitType={submitType}
                  editingRule={ruleBeingEdited}
                />
              }
              <header>
                <h1>Regras de assinatura</h1>
              </header>
              {
                internalRules.length > 0 &&
                <>
                  <ul className="inbox">
                    <li className="inbox__header">
                      <div className="inbox__cell--row-container">
                        <div style={{ paddingLeft: "10px", paddingBottom: "5px" }}><h3>QI TECH</h3></div>
                      </div>
                    </li>
                  </ul>
                  <ul className="inbox">
                    {
                      internalRules.sort((a, b) => {
                        if (mapRuleNameByStep(a.step).toLowerCase() < mapRuleNameByStep(b.step).toLowerCase()) return -1;
                        if (mapRuleNameByStep(a.step).toLowerCase() > mapRuleNameByStep(b.step).toLowerCase()) return 1;
                        return 0;
                      }).map((rule, index) => (
                        <li className="inbox__body" key={String(index + rule.step)}>
                          {
                            mapRuleTypeByStep(rule.step) === 'boolean' &&
                            <BooleanRuleCard step={mapRuleNameByStep(rule.step)} decision={mapRuleDecisionName(rule.parameters[0].decision)} description={mapRuleDecisionByStep(rule.step)} />
                          }
                          {
                            mapRuleTypeByStep(rule.step) === 'numeric' &&
                            <NumericRuleCard step={mapRuleNameByStep(rule.step)} decision={mapRuleDecisionName(rule.parameters[0].decision)} limiter={rule.parameters[0].value} description={mapRuleDecisionByStep(rule.step)} />
                          }
                          {
                            mapRuleTypeByStep(rule.step) === 'list_presence' &&
                            <ListPresenceRuleCard step={mapRuleNameByStep(rule.step)} decision={mapRuleDecisionName(rule.parameters[0].decision)} flags={rule.parameters[0].value} description={mapRuleDecisionByStep(rule.step)} />
                          }
                          {
                            mapRuleTypeByStep(rule.step) === 'risk_level' &&
                            <RiskLevelRuleCard step={mapRuleNameByStep(rule.step)} decision={mapRuleDecisionName(rule.parameters[0].decision)} level={rule.parameters[0].value ?? DEFAULT_RISK_CLASSIFICATION_LEVEL} description={mapRuleDecisionByStep(rule.step)} />
                          }
                          {
                            mapRuleTypeByStep(rule.step) === 'composite' &&
                            <CompositeRuleCard step={mapRuleNameByStep(rule.step)} decision={mapRuleDecisionName(rule.parameters[0].decision)} fallbackDecision={rule.parameters[0].fallback_decision} floor={(rule.parameters[0].value ?? [])[0]} ceiling={(rule.parameters[0].value ?? [])[1]} floorComparisonType={rule.parameters[0].floor_comparison_type} ceilingComparisonType={rule.parameters[0].ceiling_comparison_type} documentSimilarityThreshold={rule.parameters[0].document_similarity_threshold} documentComparisonType={rule.parameters[0].comparison_type} requiredOcrFlags={rule.parameters[0].required_ocr_flags ?? []} description={mapRuleDecisionByStep(rule.step)} />
                          }
                        </li>
                      ))
                    }
                  </ul>
                </>
              }

              <ul className="inbox">
                <li className="inbox__header">
                  <div className="inbox__cell--left  inbox__cell--row-container">
                    <div style={{ paddingLeft: "10px", paddingBottom: "5px" }}>
                      <h3 style={{ display: "inline" }}>{companyData.name}</h3>
                      {
                        !ruleEditingMode && userRoles.includes("manage_rules") &&
                        <button
                          style={{ marginLeft: '10px' }}
                          className="btn btn--animated btn--white"
                          onClick={(event) => activeEditingMode()}
                        >
                          <EditIcon
                            style={{ scale: "1.2", height: "1.5rem", width: "1.8rem" }}
                          />
                        </button>
                      }

                      {
                        ruleEditingMode && userRoles.includes("manage_rules") &&
                        <>
                          <TextButton onClick={cancelEditingMode} labelText='Cancelar' />
                          <TextButton onClick={saveRules} labelText='Salvar' />
                        </>
                      }

                    </div>
                  </div>
                  <div className="inbox__cell--center">
                    {
                      userRoles.includes("manage_rules") && ruleEditingMode &&
                      <BaseButton
                        label="Criar regra"
                        onClick={() => openRuleCreation()}
                        className="btn__container--center btn btn--animated btn--white"
                        isDisabled={false}
                      />
                    }
                  </div>
                  <div className="inbox__cell--right"></div>
                </li>
              </ul>

              <ul className="inbox">
                {
                  rules.sort((a, b) => {
                    if (mapRuleNameByStep(a.step).toLowerCase() < mapRuleNameByStep(b.step).toLowerCase()) return -1;
                    if (mapRuleNameByStep(a.step).toLowerCase() > mapRuleNameByStep(b.step).toLowerCase()) return 1;
                    return 0;
                  }).map((rule, index) => (
                    <li className="inbox__body" key={String(index + rule.step)}>
                      {
                        mapRuleTypeByStep(rule.step) === 'boolean' &&
                        <BooleanRuleCard step={mapRuleNameByStep(rule.step)} decision={mapRuleDecisionName(rule.parameters[0].decision)} description={mapRuleDecisionByStep(rule.step)} />
                      }
                      {
                        mapRuleTypeByStep(rule.step) === 'numeric' &&
                        <NumericRuleCard step={mapRuleNameByStep(rule.step)} decision={mapRuleDecisionName(rule.parameters[0].decision)} limiter={rule.parameters[0].value} description={mapRuleDecisionByStep(rule.step)} />
                      }
                      {
                        mapRuleTypeByStep(rule.step) === 'list_presence' &&
                        <ListPresenceRuleCard step={mapRuleNameByStep(rule.step)} decision={mapRuleDecisionName(rule.parameters[0].decision)} flags={rule.parameters[0].value} description={mapRuleDecisionByStep(rule.step)} />
                      }
                      {
                        mapRuleTypeByStep(rule.step) === 'risk_level' &&
                        <RiskLevelRuleCard step={mapRuleNameByStep(rule.step)} decision={mapRuleDecisionName(rule.parameters[0].decision)} level={rule.parameters[0].value ?? DEFAULT_RISK_CLASSIFICATION_LEVEL} description={mapRuleDecisionByStep(rule.step)} />
                      }
                      {
                        mapRuleTypeByStep(rule.step) === 'composite' &&
                        <CompositeRuleCard step={mapRuleNameByStep(rule.step)} decision={mapRuleDecisionName(rule.parameters[0].decision)} fallbackDecision={rule.parameters[0].fallback_decision} floor={(rule.parameters[0].value ?? [])[0]} ceiling={(rule.parameters[0].value ?? [])[1]} floorComparisonType={rule.parameters[0].floor_comparison_type} ceilingComparisonType={rule.parameters[0].ceiling_comparison_type} documentSimilarityThreshold={rule.parameters[0].document_similarity_threshold} documentComparisonType={rule.parameters[0].comparison_type} requiredOcrFlags={rule.parameters[0].required_ocr_flags ?? []} description={mapRuleDecisionByStep(rule.step)} />
                      }
                      {
                        ruleEditingMode && userRoles.includes("manage_rules") &&
                        <div className="inbox__cell--right" style={{ paddingBottom: "10px" }}>
                          <IconButton onClick={() => editRule(rule)}>
                            <EditIcon
                              style={{ scale: "1.2", height: "1.5rem", width: "1.8rem" }}
                            />
                          </IconButton>
                          <IconButton onClick={() => deleteRule(index)}>
                            <DeleteIcon
                              style={{ scale: "1.2", height: "1.5rem", width: "1.8rem" }}
                            />
                          </IconButton>
                        </div>

                      }
                    </li>
                  ))
                }
              </ul>
            </div>
          ) : (
            <S.RulePageContainer>
              <NoAccessPage />
            </S.RulePageContainer>
          )}
      </>
    );
  }
};
